import { useEffect, useState } from "react";
import DashSidebar from "@/components/DashSidebar";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Trash2, UserCog } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";

export default function AdminPartners() {
  const [partners, setPartners] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", company: "", phone: "" });

  const load = async () => {
    try {
      const { data } = await api.get("/partners");
      setPartners(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/partners", form);
      setForm({ name: "", email: "", password: "", company: "", phone: "" });
      setOpen(false);
      toast.success("Partner created");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const del = async (id) => {
    if (!window.confirm("Remove this partner? Their assigned leads will become unassigned.")) return;
    await api.delete(`/partners/${id}`);
    toast.success("Partner removed");
    load();
  };

  return (
    <div className="flex min-h-screen bg-[#F7F5F0]">
      <DashSidebar role="admin" />
      <main className="flex-1 lg:ml-0 ml-64 p-8 md:p-10">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-display text-3xl md:text-4xl font-semibold text-[#1A2E24]">Partners</h1>
            <p className="text-[#5C6B62] mt-1">Manage who sees which leads.</p>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#2C4C3B] hover:bg-[#1A2E24] text-white rounded-xl h-11" data-testid="new-partner-btn">
                <Plus className="w-4 h-4 mr-2" /> New partner
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-display text-2xl">Create partner</DialogTitle></DialogHeader>
              <form onSubmit={create} className="space-y-4 mt-2">
                <div>
                  <Label className="text-[#2C4C3B]">Name</Label>
                  <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 h-11 rounded-xl bg-[#F7F5F0]" data-testid="partner-name" />
                </div>
                <div>
                  <Label className="text-[#2C4C3B]">Email</Label>
                  <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 h-11 rounded-xl bg-[#F7F5F0]" data-testid="partner-email" />
                </div>
                <div>
                  <Label className="text-[#2C4C3B]">Password</Label>
                  <Input required type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1 h-11 rounded-xl bg-[#F7F5F0]" data-testid="partner-password" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[#2C4C3B]">Company</Label>
                    <Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="mt-1 h-11 rounded-xl bg-[#F7F5F0]" />
                  </div>
                  <div>
                    <Label className="text-[#2C4C3B]">Phone</Label>
                    <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1 h-11 rounded-xl bg-[#F7F5F0]" />
                  </div>
                </div>
                <Button type="submit" className="w-full bg-[#2C4C3B] hover:bg-[#1A2E24] rounded-xl h-11" data-testid="partner-create-submit">
                  Create partner
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="partners-grid">
          {partners.map((p) => (
            <div key={p.id} className="card-soft p-6" data-testid={`partner-${p.id}`}>
              <div className="flex items-start justify-between">
                <div className="w-12 h-12 rounded-xl bg-[#9CAF88]/15 flex items-center justify-center">
                  <UserCog className="w-5 h-5 text-[#2C4C3B]" />
                </div>
                <button onClick={() => del(p.id)} className="text-[#E07A5F] hover:text-[#C96B52]" data-testid={`partner-delete-${p.id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              <div className="mt-4 font-display text-lg font-semibold text-[#1A2E24]">{p.name}</div>
              <div className="text-sm text-[#5C6B62]">{p.company || "—"}</div>
              <div className="mt-3 text-sm text-[#1A2E24]">{p.email}</div>
              <div className="text-xs text-[#5C6B62]">{p.phone || "—"}</div>
            </div>
          ))}
          {partners.length === 0 && <div className="text-[#5C6B62]">No partners yet. Create one to start assigning leads.</div>}
        </div>
      </main>
    </div>
  );
}
